import { createContext, useContext, useMemo, useState } from 'react'

import { AddToPositionDialog } from '@/pages/operations/pnl-attributions/dialogs/AddToPositionDialog'
import { AddToTradeDialog } from '@/pages/operations/pnl-attributions/dialogs/AddToTradeDialog'
import { AdjustPositionDialog } from '@/pages/operations/pnl-attributions/dialogs/AdjustPositionDialog'
import { AdjustTradeDialog } from '@/pages/operations/pnl-attributions/dialogs/AdjustTradeDialog'
import { ApproveRequestDialog } from '@/pages/operations/pnl-attributions/dialogs/ApproveRequestDialog'
import { PublishAttributionsDialog } from '@/pages/operations/pnl-attributions/dialogs/PublishAttributionsDialog'
import { RefreshAttributionsDialog } from '@/pages/operations/pnl-attributions/dialogs/RefreshAttributionsDialog'
import { RejectRequestDialog } from '@/pages/operations/pnl-attributions/dialogs/RejectRequestDialog'
import { StartAttributionsDialog } from '@/pages/operations/pnl-attributions/dialogs/StartAttributionsDialog'

type PnlAttributionsAction =
  | {
      type: 'start-validating'
      params: React.ComponentProps<typeof StartAttributionsDialog>['mutationParams']
    }
  | {
      type: 'refresh-attributions'
      params: React.ComponentProps<typeof RefreshAttributionsDialog>['mutationParams']
    }
  | {
      type: 'publish-attributions'
      params: React.ComponentProps<typeof PublishAttributionsDialog>['mutationParams']
    }
  | {
      type: 'adjust-position'
      params: React.ComponentProps<typeof AdjustPositionDialog>['mutationParams']
    }
  | {
      type: 'add-to-position'
      params: React.ComponentProps<typeof AddToPositionDialog>['mutationParams']
    }
  | {
      type: 'adjust-trade'
      params: React.ComponentProps<typeof AdjustTradeDialog>['mutationParams']
    }
  | {
      type: 'add-to-trade'
      params: React.ComponentProps<typeof AddToTradeDialog>['mutationParams']
    }
  | {
      type: 'approve-request'
      params: React.ComponentProps<typeof ApproveRequestDialog>['mutationParams']
    }
  | {
      type: 'reject-request'
      params: React.ComponentProps<typeof RejectRequestDialog>['mutationParams']
    }

type GetActionParams<TType extends PnlAttributionsAction['type']> = Extract<
  PnlAttributionsAction,
  { type: TType }
>['params']

const useCreatePnlAttributionsActionsContext = () => {
  const [actionState, setActionState] = useState<PnlAttributionsAction | null>(null)

  const actions = useMemo(() => {
    return {
      openStartValidating: (params: GetActionParams<'start-validating'>) => {
        setActionState({ type: 'start-validating', params })
      },
      openRefreshAttributions: (params: GetActionParams<'refresh-attributions'>) => {
        setActionState({ type: 'refresh-attributions', params })
      },
      openPublishAttributions: (params: GetActionParams<'publish-attributions'>) => {
        setActionState({ type: 'publish-attributions', params })
      },
      openAdjustPosition: (params: GetActionParams<'adjust-position'>) => {
        setActionState({ type: 'adjust-position', params })
      },
      openAddToPosition: (params: GetActionParams<'add-to-position'>) => {
        setActionState({ type: 'add-to-position', params })
      },
      openAdjustTrade: (params: GetActionParams<'adjust-trade'>) => {
        setActionState({ type: 'adjust-trade', params })
      },
      openAddToTrade: (params: GetActionParams<'add-to-trade'>) => {
        setActionState({ type: 'add-to-trade', params })
      },
      openApproveRequest: (params: GetActionParams<'approve-request'>) => {
        setActionState({ type: 'approve-request', params })
      },
      openRejectRequest: (params: GetActionParams<'reject-request'>) => {
        setActionState({ type: 'reject-request', params })
      },
      close: () => setActionState(null),
    }
  }, [])

  return {
    actionState,
    ...actions,
  }
}

export const pnlAttributionsActionsContext = createContext<ReturnType<
  typeof useCreatePnlAttributionsActionsContext
> | null>(null)

export const usePnlAttributionsActions = () => {
  const context = useContext(pnlAttributionsActionsContext)
  if (!context) throw new Error('PnlAttributionsContext must be used within a <PnlAttributionsActionsProvider/>.')
  return context
}

export const PnlAttributionsActionsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const context = useCreatePnlAttributionsActionsContext()
  const state = context.actionState

  const dialogProps = {
    open: true,
    onClose: context.close,
  }

  return (
    <pnlAttributionsActionsContext.Provider value={context}>
      {children}
      {state?.type === 'start-validating' && <StartAttributionsDialog mutationParams={state.params} {...dialogProps} />}
      {state?.type === 'refresh-attributions' && (
        <RefreshAttributionsDialog mutationParams={state.params} {...dialogProps} />
      )}
      {state?.type === 'publish-attributions' && (
        <PublishAttributionsDialog mutationParams={state.params} {...dialogProps} />
      )}
      {state?.type === 'adjust-position' && <AdjustPositionDialog mutationParams={state.params} {...dialogProps} />}
      {state?.type === 'add-to-position' && <AddToPositionDialog mutationParams={state.params} {...dialogProps} />}
      {state?.type === 'adjust-trade' && <AdjustTradeDialog mutationParams={state.params} {...dialogProps} />}
      {state?.type === 'add-to-trade' && <AddToTradeDialog mutationParams={state.params} {...dialogProps} />}
      {state?.type === 'approve-request' && <ApproveRequestDialog mutationParams={state.params} {...dialogProps} />}
      {state?.type === 'reject-request' && <RejectRequestDialog mutationParams={state.params} {...dialogProps} />}
    </pnlAttributionsActionsContext.Provider>
  )
}
